import {FormSelect, NumericFormInput} from "../../../../components/Forms";
import {CONSUMPTION_ITEMS, MEASURES} from "../../helpers";
import {Button, GridItem, IconButton, Text, useToast} from "@chakra-ui/react";
import {MdAdd} from "react-icons/md";
import {useFieldArray} from "react-hook-form";
import {t} from "i18next";
import {Fragment, useState} from "react";
import {dimensionsKeys, showErrorToast} from "../../../../utils";
import {FaTrash} from "react-icons/fa";
import {useGetDimensions} from "../../../../hooks/query/dimensions";
import {NUMBER_OF_ACTION_COLS, NUMBER_OF_STATIC_COLS} from "./DimensionsGrid";

export const getEmptyRow = (organizationStructure, dimensionItems, scope) => {
  const customItem = {};

  dimensionsKeys.forEach(dimKey => {
    // Check if dimension is used for the current questionnaire
    if (organizationStructure.dimensions[dimKey]) {
      // Setting the predefined dimension value if it's available, if it's not, setting empty string
      customItem[`${dimKey}Item`] = dimensionItems[`${dimKey}Item`] ?? "";
    }
  });
  if(scope === "one") customItem["consumptionType"] = "";
  else customItem["electricityType"] =  "";
  customItem["consumptionValue"] = "";
  customItem["consumptionMeasure"] = "";

  return customItem;
};

function GridItemsSection({ modalData, formData }) {
  const { organizationStructure, dimensionItems, scope, fieldArrayName, consumptionTypesAttr, editData } = modalData;
  const { control, watch, register, errors, resetField } = formData;
  const toast = useToast();
  const [changedRowIndex, setChangedRowIndex] = useState(-1);
  const [initialDimensionsData, setInitialDimensionsData] = useState(null);

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });
  const fieldArrayValues = watch(fieldArrayName);

  const renderDimensionOptions = (dimKey, gridRowIndex) => {
    let dimItems = [];

    if (fieldArrayValues[gridRowIndex] && fieldArrayValues[gridRowIndex].dimensionsData) {
      dimItems.push(...fieldArrayValues[gridRowIndex].dimensionsData[dimKey].items);
    } else {
      dimItems.push(...initialDimensionsData[dimKey].items);
    }

    return dimItems
      .filter(item => item.state !== "ARCHIVED")
      .map(item => {
        return <option key={`${dimKey}Item-${gridRowIndex}-${item.id}`} value={item.id}>
          {item.name}
        </option>;
      });
  };

  const renderMeasureOptions = (gridRowIndex) => {
    const measureItems = [];
    if (scope === "one") {
      const foundMeasureItems = organizationStructure.measures.find(measure =>
        measure.consumptionTypeId === parseInt(fieldArrayValues[gridRowIndex].consumptionType))?.measures ?? [];
      measureItems.push(...foundMeasureItems);
    } else {
      const foundMeasureItems = organizationStructure.electricityMeasures.find(measure =>
          measure.electricityType === fieldArrayValues[gridRowIndex].electricityType)?.measures ?? [];
      measureItems.push(...foundMeasureItems);
    }

    return measureItems.map(optItem => {
      return <option key={`consumptionMeasure-${gridRowIndex}-${optItem.id}`} value={optItem.id}>
        {MEASURES[optItem.code] ?? optItem.name}
      </option>;
    }) ?? [];
  };

  const getDimensionsQueryParams = () => {
    const queryParams = {};

    dimensionsKeys.forEach(key => {
      if (changedRowIndex !== -1) {
        // The case when user selects an item from dimension dropdowns
        if (fieldArrayValues[changedRowIndex] && typeof fieldArrayValues[changedRowIndex][`${key}Item`] !== "undefined" &&
          fieldArrayValues[changedRowIndex][`${key}Item`] !== null && fieldArrayValues[changedRowIndex][`${key}Item`] !== "") {
          queryParams[`${key}Item`] = fieldArrayValues[changedRowIndex][`${key}Item`];
        }
      } else {
        // Initial request case

        if (editData) {
          // Initial request from edit data modal
          const dimItem = editData[`${key}Item`];
          if (dimItem !== null && typeof dimItem !== "undefined" && dimItem !== "") {
            queryParams[`${key}Item`] = dimItem;
          }
        } else {
          // Initial request from add data modal
          if (dimensionItems !== null) {
            if (dimensionItems[`${key}Item`] !== null && typeof dimensionItems[`${key}Item`] !== "undefined") {
              queryParams[`${key}Item`] = dimensionItems[`${key}Item`];
            }
          }
        }
      }
    });

    return queryParams;
  };

  useGetDimensions(getDimensionsQueryParams(),
    {
      onSuccess: data => {
        if (changedRowIndex !== -1) {
          // Storing dimensions data for each separate data row
          update(changedRowIndex, {
            ...fieldArrayValues[changedRowIndex],
            dimensionsData: data
          });
        } else {
          // Setting the initial dimensions' data, when modal is just opened
          setInitialDimensionsData(data);
        }
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      enabled: changedRowIndex !== -1 || initialDimensionsData === null
    }
  );

  return (
    initialDimensionsData !== null ? <Fragment>
      {fields.map((field, index) => {
        const gridItems = [];

        dimensionsKeys.forEach((dimKey, dimIndex) => {
          // Check if dimension is used for the current questionnaire
          if (organizationStructure.dimensions[dimKey]) {
            let dimensionItem;
            // If dimension's value in 'dimensionItems' is not null, then it has predefined value, and it is shown as a label
            // If dimension's value in 'dimensionItems' is null, then it's user populated field, and it is shown as a dropdown
            if (dimensionItems[`${dimKey}Item`] !== null) {
              // Label
              dimensionItem = <FormSelect
                {...register(`${fieldArrayName}.${index}.${dimKey}Item`)}
                onMouseDown={(e) => e.preventDefault()}
                borderWidth="0"
                margin="0"
                whiteSpace="nowrap"
                padding="0"
                width="100%"
                style={{ padding: 0 }}
                fontWeight="semibold"
                fontSize="sm"
                color="black"
                icon={<span />}
                title={organizationStructure.dimensions[dimKey].items.find(item =>
                  item.id.toString() === fieldArrayValues[index][`${dimKey}Item`]?.toString())?.name ?? ""}
              >
                {organizationStructure.dimensions[dimKey].items.map(optItem => {
                  return <option key={`${dimKey}Item-${index}-${optItem.id}`} value={optItem.id}>
                    {optItem.name}
                  </option>;
                })}
              </FormSelect>;
            } else {
              // Dropdown
              const dimensionSelector = {
                ...register(`${fieldArrayName}.${index}.${dimKey}Item`,
                  {
                    /* Dimension is required only in scope two and only if it is country related */
                    required: scope === "two" && organizationStructure.dimensions[dimKey].country ?
                      t("errors.required") : null
                  })
              };
              dimensionItem = <FormSelect
                {...register(`${fieldArrayName}.${index}.${dimKey}Item`)}
                fontSize="sm"
                fontWeight="semibold"
                errors={errors}
                onChange={(e) => {
                  dimensionSelector.onChange(e);
                  setChangedRowIndex(index);
                }}
                title={organizationStructure.dimensions[dimKey].items.find(item =>
                  item.id.toString() === fieldArrayValues[index][`${dimKey}Item`]?.toString())?.name ?? ""}
              >
                {<option key={`${dimKey}Item-${index}-empty}`} value={""}>
                  {""}
                </option>}
                {renderDimensionOptions(dimKey, index)}
              </FormSelect>;
            }

            gridItems.push(dimensionItem);
          }
        });

        if(scope === "one") {
          // Adding consumption type grid item
          const consumptionTypeSelector = {
            ...register(`${fieldArrayName}.${index}.consumptionType`, { required: t("errors.required") })
          };
          gridItems.push(<FormSelect
              {...consumptionTypeSelector}
              onChange={(e) => {
                consumptionTypeSelector.onChange(e);
                // When consumption type is changed, consumptionMeasure field is autopopulated with the first
                // available option from the measures list of the current consumption type
                const firstMeasureValue = organizationStructure.measures.find(measure =>
                    measure.consumptionTypeId === parseInt(e.target.value))?.measures?.[0]?.id ?? "";
                // resetField is used in order to reevaluate the isValid state of the field
                resetField(`${fieldArrayName}.${index}.consumptionMeasure`, { defaultValue: firstMeasureValue });
              }}
              fontSize="sm"
              fontWeight="semibold"
              errors={errors}
              title={CONSUMPTION_ITEMS[fieldArrayValues[index]?.consumptionType] ?? fieldArrayValues[index]?.consumptionType}
          >
            {organizationStructure[consumptionTypesAttr].map(optItem => {
              return <option key={`consumptionType-${index}-${optItem.id}`} value={optItem.id}>
                {CONSUMPTION_ITEMS[optItem.code] ?? optItem.name}
              </option>;
            })}
          </FormSelect>);

        } else {

          // Adding electricity type grid item
          const electricityTypeSelector = {
            ...register(`${fieldArrayName}.${index}.electricityType`, { required: t("errors.required") })
          };
          gridItems.push(<FormSelect
              {...electricityTypeSelector}
              onChange={(e) => {
                electricityTypeSelector.onChange(e);
                // When consumption type is changed, consumptionMeasure field is autopopulated with the first
                // available option from the measures list of the current consumption type
                const firstMeasureValue = organizationStructure.electricityMeasures.find(measure =>
                    measure.electricityType === e.target.value)?.measures?.[0]?.id ?? "";
                // resetField is used in order to reevaluate the isValid state of the field
                resetField(`${fieldArrayName}.${index}.consumptionMeasure`, { defaultValue: firstMeasureValue });
              }}
              fontSize="sm"
              fontWeight="semibold"
              errors={errors}
              title={CONSUMPTION_ITEMS[fieldArrayValues[index]?.electricityType] ?? fieldArrayValues[index]?.electricityType}
          >
            {organizationStructure[consumptionTypesAttr].map(optItem => {
              return <option key={`electricityType-${index}-${optItem}`} value={optItem}>
                {CONSUMPTION_ITEMS[optItem] ?? optItem}
              </option>;
            })}
          </FormSelect>);
        }


        // Adding consumption value grid item
        gridItems.push(<NumericFormInput
          {...register(`${fieldArrayName}.${index}.consumptionValue`, { required: t("errors.required") })}
          fontSize="sm"
          fontWeight="semibold"
          errors={errors}
          title={fieldArrayValues[index]?.consumptionValue ?? ""}
        />);

        // Adding consumption measure grid item
        gridItems.push(<FormSelect
          {...register(`${fieldArrayName}.${index}.consumptionMeasure`, { required: t("errors.required") })}
          fontSize="sm"
          fontWeight="semibold"
          errors={errors}
          title={MEASURES[fieldArrayValues[index]?.consumptionMeasure] ?? fieldArrayValues[index]?.consumptionMeasure}
        >
          {renderMeasureOptions(index)}
        </FormSelect>);

        // Adding row removal button if it is not edit modal
        if (!editData) {
          gridItems.push(<IconButton
            type="button"
            icon={<FaTrash size="18" />}
            width="100%"
            color="esg_red"
            variant="ghost"
            disabled={fields.length === 1}
            onClick={() => {
              setChangedRowIndex(-1);
              remove(index);
            }}
          />);
        }

        return gridItems.map((gridItem, gridItemIndex) => {
          // Total number of columns is sum of organization dimensions, static columns and action column
          const numberOfColumns = dimensionsKeys.filter(dimKey =>
            organizationStructure.dimensions[dimKey] !== null).length + NUMBER_OF_STATIC_COLS +
            (editData ? 0 : NUMBER_OF_ACTION_COLS);

          return <GridItem
            key={`${gridItemIndex}-${scope === "one" ? consumptionTypesAttr : "indirect"}`}
            borderWidth={"1px"}
            pl="4" pr="4" pt="2" pb="2"
            display="flex"
            alignItems="center"
            colSpan={editData ? 2 : gridItemIndex === numberOfColumns - 1 ? 1 : 2}
          >
            {gridItem}
          </GridItem>;
        });
      })}

      {/* Last grid item (row) is spanning over all the columns and this is how we calculate it. */}
      {/* We have 1 column (the action column) with colSpan = 1, and all other columns have colSpan = 2 */}
      {/* The columns with colSpan = 2 are all the available dimensions + three static columns (Category, Value, Measure) */}
      {!editData && <GridItem
        colSpan={(dimensionsKeys.filter(dimKey =>
          organizationStructure.dimensions[dimKey] !== null).length + NUMBER_OF_STATIC_COLS) * 2 + NUMBER_OF_ACTION_COLS}
        borderWidth={"1px"}
        pr="4" pt="2" pb="2"
        display="flex"
        alignItems="center"
      >
        <Button
          variant="ghost"
          display="flex"
          alignItems="center"
          onClick={() => {
            append(getEmptyRow(organizationStructure, dimensionItems, scope), { shouldFocus: false });
          }}
          _focus={{
            outline: "none",
            textDecoration: "underline"
          }}
        >
          <IconButton
            as="div"
            variant="outline"
            borderRadius="full"
            size="sm"
            tabIndex={-1}
            pointerEvents="none"
            flexShrink="0"
            icon={<MdAdd size="20" />}
          />
          <Text marginInlineStart="2">{t("buttons.addAnotherItem")}</Text>
        </Button>
      </GridItem>}
    </Fragment> : null
  );
}

export default GridItemsSection;